import { useState, useEffect } from "react";
import {
  Activity,
  Zap,
  Monitor,
  Download,
  RefreshCw,
  Eye,
  Target,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  Cpu,
} from "lucide-react";
import { exportMetrics } from "../lib/monitoring/performanceMonitor";
import { useToolState } from "../contexts/ToolStateContext";

interface MetricCard {
  title: string;
  value: string | number;
  unit?: string;
  status: "good" | "warning" | "error";
  icon: React.ReactNode;
  description?: string;
}

export function PerformanceDashboard() {
  const { getToolState, setToolState } = useToolState();
  const persisted = getToolState('settings');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [metrics, setMetrics] = useState<any>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState<
    "performance" | "accuracy" | "usage"
  >((persisted?.performanceSubTab as "performance" | "accuracy" | "usage") ?? "performance");

  useEffect(() => {
    const current = getToolState('settings');
    setToolState('settings', { ...current, activeTab: current?.activeTab ?? 'theme', performanceSubTab: selectedTab });
  }, [selectedTab, getToolState, setToolState]);

  useEffect(() => {
    refreshMetrics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(refreshMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const refreshMetrics = async () => {
    setIsRefreshing(true);
    try {
       
      const allMetrics = await exportMetrics();
      setMetrics(allMetrics);
    } catch (error) {
      console.error("Failed to refresh metrics:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusColor = (status: "good" | "warning" | "error") => {
    switch (status) {
      case "good":
        return "text-green-600 bg-green-50 border-green-200";
      case "warning":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "error":
        return "text-red-600 bg-red-50 border-red-200";
    }
  };

  const getPerformanceStatus = (
    metric: string,
    value: number,
  ): "good" | "warning" | "error" => {
    switch (metric) {
      case "FCP":
      case "LCP":
        return value < 2500 ? "good" : value < 4000 ? "warning" : "error";
      case "FID":
        return value < 100 ? "good" : value < 300 ? "warning" : "error";
      case "CLS":
        return value < 0.1 ? "good" : value < 0.25 ? "warning" : "error";
      case "averageDuration":
        return value < 50 ? "good" : value < 100 ? "warning" : "error";
      default:
        return "good";
    }
  };

  const exportData = () => {
    const data = exportMetrics();
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chromis-metrics-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Loading metrics...
      </div>
    );
  }

  // Performance metrics cards
  const performanceCards: MetricCard[] = [
    {
      title: "First Contentful Paint",
      value: metrics.summary.webVitals.FCP || 0,
      unit: "ms",
      status: getPerformanceStatus("FCP", metrics.summary.webVitals.FCP || 0),
      icon: <Eye className="w-5 h-5" />,
      description: "Time to first content render",
    },
    {
      title: "Largest Contentful Paint",
      value: metrics.summary.webVitals.LCP || 0,
      unit: "ms",
      status: getPerformanceStatus("LCP", metrics.summary.webVitals.LCP || 0),
      icon: <Monitor className="w-5 h-5" />,
      description: "Time to largest content render",
    },
    {
      title: "First Input Delay",
      value: metrics.summary.webVitals.FID || 0,
      unit: "ms",
      status: getPerformanceStatus("FID", metrics.summary.webVitals.FID || 0),
      icon: <Zap className="w-5 h-5" />,
      description: "Input responsiveness time",
    },
    {
      title: "Cumulative Layout Shift",
      value: metrics.summary.webVitals.CLS || 0,
      unit: "",
      status: getPerformanceStatus("CLS", metrics.summary.webVitals.CLS || 0),
      icon: <Activity className="w-5 h-5" />,
      description: "Visual stability score",
    },
    {
      title: "Average Simulation Time",
      value: Math.round(metrics.summary.substrateOperations.averageDuration),
      unit: "ms",
      status: getPerformanceStatus(
        "averageDuration",
        metrics.summary.substrateOperations.averageDuration,
      ),
      icon: <Clock className="w-5 h-5" />,
      description: "Substrate simulation performance",
    },
    {
      title: "Memory Usage",
      value: Math.round(metrics.summary.substrateOperations.averageMemoryUsage),
      unit: "MB",
      status:
        metrics.summary.substrateOperations.averageMemoryUsage < 100
          ? "good"
          : "warning",
      icon: <Cpu className="w-5 h-5" />,
      description: "Average memory per operation",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Performance Dashboard
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Real-time monitoring of Chromis performance metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshMetrics}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
          <button
            onClick={exportData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setSelectedTab("performance")}
          className={`px-4 py-2 font-medium ${
            selectedTab === "performance"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Performance
        </button>
        <button
          onClick={() => setSelectedTab("accuracy")}
          className={`px-4 py-2 font-medium ${
            selectedTab === "accuracy"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Accuracy
        </button>
        <button
          onClick={() => setSelectedTab("usage")}
          className={`px-4 py-2 font-medium ${
            selectedTab === "usage"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Usage Analytics
        </button>
      </div>

      {/* Performance Tab */}
      {selectedTab === "performance" && (
        <div className="space-y-6">
          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {performanceCards.map((card, index) => (
              <div
                key={index}
                className={`p-4 border rounded-lg ${getStatusColor(card.status)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {card.icon}
                    <div>
                      <h3 className="font-semibold">{card.title}</h3>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold">{card.value}</span>
                        {card.unit && (
                          <span className="text-sm">{card.unit}</span>
                        )}
                      </div>
                      {card.description && (
                        <p className="text-sm opacity-75 mt-1">
                          {card.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {card.status === "good" && (
                      <CheckCircle className="w-5 h-5" />
                    )}
                    {card.status === "warning" && (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    {card.status === "error" && (
                      <AlertCircle className="w-5 h-5" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Substrate Operations Summary */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Substrate Operations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Operations
                </p>
                <p className="text-2xl font-bold">
                  {metrics.summary.substrateOperations.total}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Success Rate
                </p>
                <p className="text-2xl font-bold">
                  {Math.round(
                    metrics.summary.substrateOperations.successRate * 100,
                  )}
                  %
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Average Duration
                </p>
                <p className="text-2xl font-bold">
                  {Math.round(
                    metrics.summary.substrateOperations.averageDuration,
                  )}
                  ms
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Long Tasks
                </p>
                <p className="text-2xl font-bold">
                  {metrics.summary.longTasks}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accuracy Tab */}
      {selectedTab === "accuracy" && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Color Accuracy Metrics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Measurements
                </p>
                <p className="text-2xl font-bold">
                  {metrics.accuracySummary.totalMeasurements}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Average ΔE
                </p>
                <p className="text-2xl font-bold">
                  {metrics.accuracySummary.averageDeltaE}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Pass Rate (≤2.0)
                </p>
                <p className="text-2xl font-bold">
                  {Math.round(metrics.accuracySummary.passRate * 100)}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Below Target 5.0
                </p>
                <p className="text-2xl font-bold">
                  {metrics.accuracySummary.belowTarget5}
                </p>
              </div>
            </div>
          </div>

          {/* Accuracy Target Status */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="font-semibold mb-3">Accuracy Target Status</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span>ΔE ≤ 2.0 Target</span>
                </div>
                <span className="font-semibold">
                  {metrics.accuracySummary.belowTarget2} measurements
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  <span>ΔE ≤ 5.0 Acceptable</span>
                </div>
                <span className="font-semibold">
                  {metrics.accuracySummary.belowTarget5} measurements
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Analytics Tab */}
      {selectedTab === "usage" && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Usage Analytics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Actions
                </p>
                <p className="text-2xl font-bold">
                  {metrics.usageAnalytics.totalActions}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Most Used Feature
                </p>
                <p className="text-2xl font-bold capitalize">
                  {metrics.usageAnalytics.mostUsedFeature}
                </p>
              </div>
            </div>
          </div>

          {/* Feature Usage */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="font-semibold mb-3">Feature Usage</h4>
            <div className="space-y-2">
              {Object.entries(metrics.usageAnalytics.featureCounts).map(
                ([feature, count]) => (
                  <div
                    key={feature}
                    className="flex items-center justify-between p-2"
                  >
                    <span className="capitalize">{feature}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${(Number(count) / metrics.usageAnalytics.totalActions) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium w-8 text-right">
                        {Number(count)}
                      </span>
                    </div>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
